import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import {
  LOCKUP_VIEWBOX,
  SYMBOL,
  SYMBOL_REVERSED,
  SYMBOL_SMALL,
  type SymbolGeometry,
  WORDMARK,
} from "./paths.ts";

/** At this size and below the symbol uses its small cut (GUIDELINES.md). */
export const SMALL_CUT_MAX = 24;
/** The horizontal lockup's minimum on-screen width (GUIDELINES.md). */
export const LOCKUP_MIN_WIDTH = 96;
const [, , LOCKUP_W = 0, LOCKUP_H = 1] = LOCKUP_VIEWBOX.split(" ").map(Number);
/** Clear space is one dot diameter: 80 units of the 256-unit symbol. */
const DOT_UNITS = 80;

/** How the mark is named: by `label` (default "ESI"), or hidden when adjacent text names it. */
interface Naming {
  label?: string;
  decorative?: boolean;
}

function Geometry({ shape, className }: { shape: SymbolGeometry; className?: string }) {
  return (
    <g className={className}>
      <path d={shape.brackets} className="fill-logo-ink" />
      <path d={shape.dot} className="fill-logo-dot" />
    </g>
  );
}

/**
 * The symbol `[•]`. Above 24 px both geometries render and the theme shows one: regular on light,
 * reversed on dark (manual theme included, which an `<img>` SVG could not follow). At 24 px and
 * below it is the small cut, in the theme's colours.
 */
export function EsiSymbol({
  size,
  className,
  label = "ESI",
  decorative = false,
}: { size: number; className?: string } & Naming) {
  return (
    <svg
      viewBox="0 0 256 256"
      width={size}
      height={size}
      className={cn("shrink-0", className)}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative || undefined}
    >
      {size <= SMALL_CUT_MAX ? (
        <Geometry shape={SYMBOL_SMALL} />
      ) : (
        <>
          <Geometry shape={SYMBOL} className="dark:hidden" />
          <Geometry shape={SYMBOL_REVERSED} className="hidden dark:inline" />
        </>
      )}
    </svg>
  );
}

/** The horizontal lockup `[•] ESI`, never narrower than its 96 px minimum. */
export function EsiLockup({
  width,
  className,
  label = "ESI",
  decorative = false,
}: { width: number; className?: string } & Naming) {
  const w = Math.max(width, LOCKUP_MIN_WIDTH);
  return (
    <svg
      viewBox={LOCKUP_VIEWBOX}
      width={w}
      height={Number(((w * LOCKUP_H) / LOCKUP_W).toFixed(2))}
      className={cn("block shrink-0", className)}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative || undefined}
    >
      <Geometry shape={SYMBOL} className="dark:hidden" />
      <Geometry shape={SYMBOL_REVERSED} className="hidden dark:inline" />
      <g className="fill-logo-ink">
        {WORDMARK.map((glyph) => (
          <path key={glyph.transform} d={glyph.d} transform={glyph.transform} />
        ))}
      </g>
    </svg>
  );
}

/** One dot diameter, in px, for a lockup of this width (its clear space). */
export function lockupClearSpace(width: number): number {
  const w = Math.max(width, LOCKUP_MIN_WIDTH);
  return Math.round((((w * LOCKUP_H) / LOCKUP_W) * DOT_UNITS) / 256);
}

/**
 * The lockup's clear space as a margin on the given sides, so a placement cannot crowd it. In a
 * 48 px bar only the side facing a control needs it: the bar's edges are ground (decision D2).
 */
export function clearSpace(width: number, sides: "start" | "end" | "inline"): CSSProperties {
  const space = lockupClearSpace(width);
  return {
    marginInlineStart: sides === "end" ? undefined : space,
    marginInlineEnd: sides === "start" ? undefined : space,
  };
}
